import React, { useState } from 'react';
import { Button, InputNumber, Select, message, Row, Col, Space, Table } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { usePhaserConfig } from '../contexts/PhaserConfigContext';
import { TSprite } from '../../../models/objects/sprite';
import { CharacterConfig } from '../types/config';
import { getImagePath } from '../../../utils/format/valueFormat';
import { SpriteAnimationPreview } from '../../../pages/admin/sprites/SpriteAnimationPreview';

interface CharacterConfiguratorProps {
    sprites: TSprite[];
}

const { Option } = Select;

const CharacterConfigurator: React.FC<CharacterConfiguratorProps> = ({ sprites }) => {
    const { characters, addCharacter, updateCharacter, removeCharacter } = usePhaserConfig();

    const [selectedSpriteId, setSelectedSpriteId] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentCharacter, setCurrentCharacter] = useState<CharacterConfig | null>(null);
    const [isModified, setIsModified] = useState<boolean>(false);

    // ==============================================
    // 1) SAUVEGARDE (AJOUT / UPDATE) DU PERSO
    // ==============================================
    const handleSaveCharacter = () => {
        if (!selectedSpriteId) {
            message.warning("Veuillez sélectionner un sprite.");
            return;
        }

        const dbChar = sprites.find((ch) => ch.id === selectedSpriteId);
        if (!dbChar) {
            message.error("Sprite introuvable dans la liste BDD.");
            return;
        }
        // Génération du CharacterConfig
        const newChar: CharacterConfig = {
            // => On génère un nouvel ID si le currentCharacter n'en a pas
            id: currentCharacter?.id || crypto.randomUUID(),

            // idPhaser => unique key pour Phaser
            idPhaser: currentCharacter?.idPhaser ||
                // ex: "Captain_America_12" 
                `${dbChar.name.replaceAll(" ", "_")}`,

            name: dbChar.name,
            x: currentCharacter?.x || 0,
            y: currentCharacter?.y || 0,
            width: currentCharacter?.width || 50,
            height: currentCharacter?.height || 50,

            image_url: dbChar.image_url,
            animations: dbChar.animations || [],
        };

        if (currentCharacter && currentCharacter.id) {
            updateCharacter(currentCharacter.id, newChar);
        } else {
            addCharacter(newChar);
        }

        // Reset form
        setIsEditing(false);
        setCurrentCharacter(null);
        setSelectedSpriteId(null);
        setIsModified(false);
    };

    // ==============================================
    // 2) DÉTECTION DES CHANGEMENTS (pour le form)
    // ==============================================
    const handleFieldChange = (field: keyof CharacterConfig, value: number) => {
        setCurrentCharacter((prev) => ({ ...prev!, [field]: value }));
        setIsModified(true);
    };

    // ==============================================
    // 3) AFFICHAGE DE LA GRILLE DE PERSOS
    // ==============================================
    const renderCharacterTable = () => {
        /** Colonnes minimalistes (pas d’entête) */
        const columns = [
            /* Aperçu animé du sprite */
            {
                dataIndex: 'preview',
                width: 50,
                render: (_: any, char: CharacterConfig) => {
                    const idleAnim = char.animations.find(a => a.state === 'idle');
                    return idleAnim ? (
                        <SpriteAnimationPreview
                            imageUrl={getImagePath(char.image_url)}
                            frameWidth={idleAnim.frame_width}
                            frameHeight={idleAnim.frame_height}
                            frameCount={idleAnim.frame_count}
                            frameRate={idleAnim.frame_rate}
                            offsetX={idleAnim.offset_x}
                            offsetY={idleAnim.offset_y}
                            scale={50}
                        />
                    ) : null;
                },
            },
            /* Nom + ID sous‑titre */
            {
                dataIndex: 'name',
                render: (_: any, char: CharacterConfig) => (
                    <div>
                        <strong>{char.name}</strong>
                        <div style={{ fontSize: 12, color: '#888' }}>ID : {char.id}</div>
                    </div>
                ),
            },
            /* Actions */
            {
                key: 'actions',
                align: 'right' as const,
                width: 90,
                render: (_: any, char: CharacterConfig) => (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => {
                                setCurrentCharacter(char);
                                const maybeSprite = sprites.find(
                                    s => getImagePath(s.image_url) === getImagePath(char.image_url),
                                );
                                if (maybeSprite) setSelectedSpriteId(maybeSprite.id);
                                setIsEditing(true);
                            }}
                        />
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => removeCharacter(char.id)}
                        />
                    </Space>
                ),
            },
        ];

        return (
            <>
                {/* Bouton “Ajouter” aligné à droite, juste au‑dessus du tableau */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setCurrentCharacter(null);
                            setSelectedSpriteId(null);
                            setIsEditing(true);
                        }}
                    >
                        Ajouter
                    </Button>
                </div>

                <Table<CharacterConfig>
                    columns={columns}
                    dataSource={characters}
                    rowKey="id"
                    pagination={false}
                    showHeader={false}   // 🌱 Pas d’en‑tête
                    size="small"         // 🌱 Table compacte
                    bordered={false}     // 🌱 Sans bordure pour plus de discrétion
                />
            </>
        );
    };


    // ==============================================
    // 4) AFFICHAGE DU FORMULAIRE
    // ==============================================
    const renderConfigForm = () => {
        // Récupérer la config “sprite” (BDD) actuellement sélectionnée
        const spriteData = selectedSpriteId
            ? sprites.find((s) => s.id === selectedSpriteId)
            : null;



        return (
            <div>
                {/* Sélecteur du sprite, avec image + nom */}
                <Select
                    placeholder="Choisir un sprite"
                    style={{ width: '100%', marginBottom: '16px' }}
                    value={selectedSpriteId}
                    onChange={(value: number) => {
                        setSelectedSpriteId(value);
                        setIsModified(true);
                    }}
                    optionLabelProp="label" // pour n'afficher que le nom en sélection
                >
                    {sprites.map((sprite) => {
                        const idleAnim = sprite.animations?.find(a => a.state === 'idle');
                        return (
                            <Option key={sprite.id} value={sprite.id} label={sprite.name}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {idleAnim && (
                                        <SpriteAnimationPreview
                                            imageUrl={getImagePath(sprite.image_url)}
                                            frameWidth={idleAnim.frame_width}
                                            frameHeight={idleAnim.frame_height}
                                            frameCount={idleAnim.frame_count}
                                            frameRate={idleAnim.frame_rate}
                                            offsetX={idleAnim.offset_x}
                                            offsetY={idleAnim.offset_y}
                                            scale={40}
                                        />
                                    )}
                                    <span>{sprite.name}</span>
                                </div>
                            </Option>
                        );
                    })}
                </Select>



                {/* Inputs en 2 colonnes : position X/Y */}
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Position X"
                            value={currentCharacter?.x}
                            onChange={(value) => handleFieldChange('x', value || 0)}
                            max={999}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Position Y"
                            value={currentCharacter?.y}
                            onChange={(value) => handleFieldChange('y', value || 0)}
                            max={999}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                {/* Inputs en 2 colonnes : width/height */}
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Largeur"
                            value={currentCharacter?.width}
                            onChange={(value) => handleFieldChange('width', value || 50)}
                            max={99}
                            min={1}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Hauteur"
                            value={currentCharacter?.height}
                            onChange={(value) => handleFieldChange('height', value || 50)}
                            max={99}
                            min={1}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                {/* Boutons centrés */}
                <div style={{ textAlign: 'center' }}>
                    <Button type="primary" onClick={handleSaveCharacter}>
                        {isModified || currentCharacter ? "Enregistrer" : "Ajouter"}
                    </Button>
                    <Button style={{ marginLeft: '8px' }} onClick={() => setIsEditing(false)}>
                        Annuler
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div>
            {isEditing ? renderConfigForm() : renderCharacterTable()}
        </div>
    );
};

export default CharacterConfigurator;
